import React from 'react';

export type StatusBadgeType = 'success' | 'error' | 'warning' | 'info';

/**
 * How much of the badge the glyph fills.
 *
 * `default` is half the diameter, which is the right proportion at the sizes a badge is
 * usually drawn. `bold` is for the small end: at the 14px a table row gives it, half a
 * badge is a mark under 3px tall, and the tone is doing all the work.
 *
 * `bold` takes the glyph's box to the full diameter rather than merely most of it, so the
 * box and the badge share an edge. Anything short of that leaves a fractional margin at odd
 * sizes (13px inside 14px), which the browser rounds onto one side and draws off centre.
 * Nothing collides with the edge: the furthest point of any glyph is the tick's, at 86% of
 * the radius.
 */
export type StatusBadgeGlyph = 'default' | 'bold';

export interface StatusBadgeProps {
    type?: StatusBadgeType;
    size?: number;
    glyph?: StatusBadgeGlyph;
    className?: string;
    'aria-label'?: string;
}

// Use the same foreground hues as the matching text tokens so the badge reads as
// the same green/red/etc. as the copy beside it (the *-background tokens are blown
// out in dark mode).
const BACKGROUNDS: Record<StatusBadgeType, string> = {
    success: 'var(--malachite-text-success, rgb(2, 122, 66))',
    error: 'var(--malachite-text-error, rgb(237, 53, 54))',
    warning: 'var(--malachite-text-warning, rgb(150, 88, 10))',
    info: 'var(--malachite-tag-primary-foreground, rgb(37, 130, 234))',
};

// Filled glyphs on a 24×24 grid. They must be filled shapes rather than strokes: the svg
// sets `fill="currentColor"` and the paths inherit it, so `color` is what paints them.
//
// All four span y 5 to 19, so no type reads as a smaller badge than another. The bar and
// dot of `warning`/`info` are drawn slightly wider than the diagonal stroke of the other
// two, because an upright shape of the same thickness reads lighter than a diagonal one.
const GLYPHS: Record<StatusBadgeType, React.ReactNode> = {
    success: <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />,
    error: (
        <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    ),
    warning: <path d="M10.75 5h2.5v9.5h-2.5zm0 11.5h2.5v2.5h-2.5z" />,
    info: <path d="M10.75 5h2.5v2.5h-2.5zm0 4.5h2.5v9.5h-2.5z" />,
};

/** How much of the badge's diameter the glyph's box takes, per `glyph`. */
const GLYPH_SCALES: Record<StatusBadgeGlyph, number> = {
    default: 0.5,
    bold: 1,
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
    type = 'success',
    size = 48,
    glyph = 'default',
    className = '',
    'aria-label': ariaLabel,
}) => {
    const iconSize = Math.round(size * GLYPH_SCALES[glyph]);

    return (
        <div
            className={`flex items-center justify-center rounded-full ${className}`}
            // The circle and the centring are repeated inline rather than left to the
            // Tailwind above, so the badge survives outside a shadow root. A consumer that
            // drops one in beside ordinary page markup would otherwise get a square with
            // the glyph jammed in a corner, and be forced to wrap it in a `ThemeScope` or
            // `MalachiteContext` purely to get its shape back.
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                width: `${size}px`,
                height: `${size}px`,
                backgroundColor: BACKGROUNDS[type],
            }}
        >
            <svg
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                // Stated here rather than left to the library's `svg path` rule, which is
                // scoped to the shadow root: outside one the paths fall back to their
                // initial black and the glyph disappears into a dark badge.
                fill="currentColor"
                // `role="img"` with `aria-label` names the badge without an SVG `<title>`,
                // which browsers render as a native tooltip: on a badge that a consumer has
                // already wrapped in a real one, that fired a second square-edged box
                // alongside it and read the tone name out as its text.
                role="img"
                aria-label={ariaLabel ?? type}
                style={{ color: '#fff' }}
            >
                {GLYPHS[type]}
            </svg>
        </div>
    );
};

export default StatusBadge;
export { StatusBadge };
